import { useCallback, useEffect, useState } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import { AttributesOption } from 'src/sections/view_checkbook/PeriodViewContext';
import {
  CheckbookLineItemError,
  CheckbookLineItemInfo,
  CheckbookLineItemWarning,
} from 'src/sections/view_checkbook/useCheckbookPeriodErrors';
import { budgetPercentageCal, round } from 'src/utils';
import axiosInstance from 'src/utils/axios';
import { GLCodePeriodDepartment, GLCodeYearDepartment } from './useGlCodeDepartmentApi';
import { Card } from './useGlCodesBudgetApi';
export interface GlApproval {
  status: number | null;
  clientIds: number[];
}

export interface GLCodePeriodBudgetResponse {
  id: number;
  code: string;
  description: string;
  remainingBudgetAmount: number;
  budgetAmount: number;
  holdAmount: number;
  spentAmount: number;
  isActive: boolean;
  isFixed: boolean;
  isDeleted?: boolean;
  hasHoldTransactionData?: boolean;
  hasSpentTransactionData?: boolean;
  remainingBudgetAmountPercentage: number;
  selected: boolean;
  isNewGlCode?: boolean;
  rowId: string;
  errors?: CheckbookLineItemError;
  warnings?: CheckbookLineItemWarning;
  informations?: CheckbookLineItemInfo;
  hasErrorsOrWarnings?: boolean;
  card: Card;
  newAmount: number;
  periodBudgets: number;
  departments: GLCodePeriodDepartment[];
  department?: string;
  endingBalance?: number;
  committedAmount?: number;
  initialBudget?: number | null;
  remainingInitialBudgetAmount?: null | number | string;
  remainingInitialBudgetAmountPercentage?: null | number | string;
  _initialBudget?: number | null;
  invoiceGlApproval?: GlApproval;
  poGlApproval?: GlApproval;
  invoiceApproval?: string | null;
  poApproval?: string | null;
  sortKey: string;
}

export const ApprovalStatus: AttributesOption[] = ['OFF', 'Required', 'Exempt'];

export const getApprovalStatus = (status: number | null | undefined) =>
  status !== null && status !== undefined ? ApprovalStatus[status] : null;

const useGLCodePeriodBudgetApi = (companyId: string, department: GLCodeYearDepartment) => {
  const [departmentFilteredGlCodePeriodBudget, setDepartmentFilteredGlCodePeriodBudget] = useState<
    GLCodePeriodBudgetResponse[]
  >([]);

  const call = useCallback(
    async (glPeriodId?: number) => {
      if (!companyId) {
        return [];
      }
      try {
        const res = await axiosInstance.get<GLCodePeriodBudgetResponse[]>(
          api.glCodePeriodBudget(companyId, glPeriodId)
        );
        const data: GLCodePeriodBudgetResponse[] = res.data?.map((item) => {
          const remainingBudgetAmountPercentage = round(
            budgetPercentageCal(item.remainingBudgetAmount, item.budgetAmount),
            2
          );

          const initialBudget = item.initialBudget || 0;
          const committedAmount = item.committedAmount || 0;
          const holdAmount = item.holdAmount || 0;
          const spentAmount = item.spentAmount || 0;
          const totalBalances = committedAmount + holdAmount + spentAmount;
          const remainingInitialBudgetAmountValue = initialBudget - totalBalances;
          const remainingInitialBudgetAmountPercentageValue = round(
            budgetPercentageCal(remainingInitialBudgetAmountValue, item.initialBudget || 0),
            2
          );

          const sortKey = `${item.code}-${item.description}`.toLowerCase();

          return {
            ...item,
            holdAmount: round(item.holdAmount, 2),
            spentAmount: round(item.spentAmount, 2),
            committedAmount: round(Number(item.committedAmount), 2),
            remainingBudgetAmount: round(item.remainingBudgetAmount, 2),
            remainingBudgetAmountPercentage,
            selected: false,
            rowId: uuid(),
            card: 'subtract',
            newAmount: 0,
            _initialBudget: item.initialBudget,
            budgetAmount: item.budgetAmount ?? 0,
            remainingInitialBudgetAmount:
              item.initialBudget === null || item.initialBudget === undefined
                ? ''
                : round(Number(remainingInitialBudgetAmountValue), 2),

            remainingInitialBudgetAmountPercentage:
              item.initialBudget === null || item.initialBudget === undefined
                ? ''
                : remainingInitialBudgetAmountPercentageValue,

            invoiceApproval:
              item?.invoiceGlApproval?.status !== undefined
                ? getApprovalStatus(item.invoiceGlApproval.status)
                : null,
            poApproval:
              item?.poGlApproval?.status !== undefined
                ? getApprovalStatus(item.poGlApproval.status)
                : null,
            label: `${item?.code} - ${item?.description}`,
            sortKey,
          };
        });
        return data;
      } catch (error) {
        throw new Error(error);
      }
    },
    [companyId]
  );

  const { isLoading, value, execute } = useAsync(
    call,
    false,
    undefined,
    [] as GLCodePeriodBudgetResponse[]
  );

  useEffect(() => {
    const data =
      department?.departmentId === 0
        ? value
        : value.filter((item) =>
            item.departments.some(
              (dep: GLCodePeriodDepartment) => dep.departmentId === department.departmentId
            )
          );
    setDepartmentFilteredGlCodePeriodBudget(data);
  }, [value, department]);

  return {
    isLoading,
    glCodePeriodBudget: departmentFilteredGlCodePeriodBudget,
    setGlCodePeriodBudget: setDepartmentFilteredGlCodePeriodBudget,
    getGlCodePeriodBudget: execute,
    allDeptGLCodes: value,
  };
};

export default useGLCodePeriodBudgetApi;
